import React, { useState, useRef, useEffect } from "react";
import DynamicForm, { FieldConfig } from "./DynamicForm";
import { useTheme } from "../../context/ThemeContext";
import { useNotification } from "../../context/NotificationContext";
import { fetchData } from "../../services/FetchDataService";
import { toSnakeCase } from "../utils/toSnakeCase";
import { FieldType } from "../interfaces/FieldType";
import MultiItemForm from "./MultiItemForm";
import { Step } from "./StepperForm";
import { convertFileToBase64, tryConvertToBase64 } from "../utils/toBase64File";

export interface StepStatus {
  current_step: string;
  next_step: string;

  [key: string]: string | boolean;
}

export interface StepperFormProps {
  steps: Step[];
  onSubmit: (data: { [key: string]: any }) => void;
  onCancel?: () => void;
  client?: any;
  stepStatus?: StepStatus;
  filterID?: number;
  output?: (response: any, step: Step, stepIndex: number) => void;
}

const AccordionForm: React.FC<StepperFormProps> = ({
  steps,
  onSubmit,
  onCancel,
  client,
  stepStatus,
  filterID,
  output,
}) => {
  const { theme } = useTheme();
  const { success, error } = useNotification();

  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [stepDefaults, setStepDefaults] = useState<{ [key: number]: any }>({});
  const formRefs = useRef<(null | { submitForm: () => void })[]>([]);

  useEffect(() => {
    if (steps.length > 0) {
      setExpandedIndexes([0]);
    }
  }, [steps.length]);

  useEffect(() => {
    async function fetchStepData(stepIndex: number, step: Step) {
      if (!filterID || !client || !step.endpoint_get) return;

      try {
        const response = await fetchData(client, step.endpoint_get, {
          id: filterID,
        });

        if (response.data) {
          // Suppose "Contact Investigator" returns an array
          if (Array.isArray(response.data)) {
            // 1) We'll map over each item in that array...
            const patchedItems = response.data.map(
              (item: { [x: string]: { [x: string]: any } }) => {
                const patchedItem: any = { ...item };

                // 2) For each field in the step.fields, apply patchFrom logic
                step.fields.forEach((field) => {
                  if (!field.patchFrom) return; // skip any fields without patchFrom

                  const { objectKey, valueKey, labelKey } = field.patchFrom;
                  // e.g. objectKey = "investigator_role",
                  //      valueKey = "id",
                  //      labelKey = "name"

                  if (item[objectKey]) {
                    patchedItem[field.key] = item[objectKey][valueKey];
                    patchedItem[`${field.key}_label`] =
                      item[objectKey][labelKey];
                  }
                });

                return patchedItem;
              }
            );

            // Finally store that patchedItems array in stepDefaults
            setStepDefaults((prev) => ({
              ...prev,
              [stepIndex]: {
                ...prev[stepIndex],
                items: patchedItems,
              },
            }));
          } else {
            // This is the SINGLE-object scenario
            const patched: { [key: string]: any } = {};
            step.fields.forEach((field) => {
              if (field.patchFrom) {
                const { objectKey, valueKey, labelKey } = field.patchFrom;
                const nested = response.data[objectKey];
                if (nested) {
                  patched[field.key] = nested[valueKey];
                  patched[`${field.key}_label`] = nested[labelKey];
                }
              } else if (response.data[field.key] !== undefined) {
                patched[field.key] = response.data[field.key];
              }
            });
            setStepDefaults((prev) => ({
              ...prev,
              [stepIndex]: patched,
            }));
          }
        }
      } catch (err) {
        console.error(`Error fetching data for ${step.title}:`, err);
      }
    }

    expandedIndexes.forEach((index) => {
      if (!stepDefaults[index]) {
        fetchStepData(index, steps[index]);
      }
    });
  }, [expandedIndexes, steps, client, filterID]);

  useEffect(() => {
    if (!stepStatus) return;

    const newCompleted = new Set<number>();
    steps.forEach((step, index) => {
      if (step.statusKey) {
        const keySnake = toSnakeCase(step.statusKey);
        if (stepStatus[keySnake] === true) {
          newCompleted.add(index);
        }
      }
    });
    setCompletedSteps(newCompleted);

    const currentKey = stepStatus.current_step?.toLowerCase() || "";
    const foundIndex = steps.findIndex(
      (s) => s.statusKey && s.statusKey.toLowerCase() === currentKey
    );
    if (foundIndex !== -1) {
      setExpandedIndexes([foundIndex]);
    }
  }, [stepStatus, steps]);

  const handleStepSubmit = async (
    stepData: { [key: string]: any },
    step: Step,
    stepIndex: number,
    actionType: string = "submit"
  ) => {
    for (const field of step.fields) {
      let value = stepData[field.key];

      // Normalize checkbox
      if (field.type === FieldType.CHECKBOX) {
        stepData[field.key] =
          typeof value === "string" ? value.toLowerCase() === "true" : !!value;
      }

      // Normalize number
      else if (field.inputType === "number") {
        if (Array.isArray(value)) {
          stepData[field.key] = value.map((v: any) =>
            v !== "" && v !== null ? parseInt(v, 10) : null
          );
        } else if (value !== "" && value !== null) {
          stepData[field.key] = parseInt(value, 10);
        }
      }

      // Normalize date
      else if (field.inputType === "date" && typeof value === "string") {
        stepData[field.key] = value
          ? new Date(`${value}T00:00:00Z`).toISOString()
          : null;
      }

      // Handle 'id'
      if (field.key === "id") {
        if (value === "" || value === null || value === undefined) {
          delete stepData.id;
        } else if (typeof value === "string") {
          const parsed = parseInt(value, 10);
          stepData.id = isNaN(parsed) ? undefined : parsed;
        }
      }
    }

    // Remove ID if creating new
    if (!filterID && "id" in stepData) {
      delete stepData.id;
    }

    let payload: any;

    // Handle array vs object payload
    if (Array.isArray(stepData)) {
      const requiredKeys = step.fields
        .filter((f) => f.validations?.required)
        .map((f) => f.key);

      payload = stepData
        .filter((item) =>
          requiredKeys.some((key) => {
            const value = item[key];
            return (
              value !== undefined &&
              value !== null &&
              String(value).trim() !== ""
            );
          })
        )
        .map((item) => ({
          ...item,
          ...step.additionalValues,
          action: step.action || actionType,
        }));
    } else {
      payload = {
        ...step.additionalValues,
        ...stepData,
        action: step.action || actionType,
      };
    }

    const endpoint = filterID
      ? step.endpoint_update || step.endpoint_create
      : step.endpoint_create;

    try {
      const response = await client.post(endpoint, payload);

      if (response.code === 200) {
        if (output) output(response.data, step, stepIndex);
        setCompletedSteps((prev) => new Set(prev).add(stepIndex));
        success(`${step.title} saved successfully`);

        if (stepIndex === steps.length - 1 && actionType === "submit") {
          onSubmit(payload);
        } else {
          setExpandedIndexes((prev) => [
            ...prev.filter((i) => i !== stepIndex),
            stepIndex + 1,
          ]);
        }
      }
    } catch (err) {
      console.error("Error saving step:", err);
      error(`Failed to save ${step.title}`);
    }
  };

  const toggleStep = (index: number) => {
    setExpandedIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    <div className="w-full py-4 mb-10">
      {steps.map((step, index) => {
        const isExpanded = expandedIndexes.includes(index);
        const isCompleted = completedSteps.has(index);
        const defaultValues = {
          ...step.initialValues,
          ...step.additionalValues,
          ...stepDefaults[index],
        };
        step.fields.forEach((field) => {
          if (
            field.type === FieldType.CHECKBOX &&
            defaultValues[field.key] === undefined
          ) {
            defaultValues[field.key] = false;
          }
        });

        function renderStepContent(step: Step, index: number) {
          const defaultValues = stepDefaults[index] || {};
          if (
            step.title === "Contact Investigator" ||
            step.title === "Study Result Publication"
          ) {
            const itemsArray = defaultValues.items || [];

            return (
              <MultiItemForm
                title={step.title}
                fields={step.fields}
                items={itemsArray}
                onChange={(updatedArr) => {
                  // Keep the local array in your stepDefaults for final merging
                  setStepDefaults((prev) => ({
                    ...prev,
                    [index]: { ...prev[index], items: updatedArr },
                  }));
                }}
                // The key part: pass handleStepSubmit, step, index
                onSaveAll={async (localItems) => {
                  const normalizedItems = await Promise.all(
                    localItems.map(async (item) => {
                      const normalizedItem = { ...item };

                      for (const field of step.fields) {
                        const value = normalizedItem[field.key];

                        if (
                          field.type === FieldType.FILE &&
                          value instanceof File
                        ) {
                          try {
                            normalizedItem[field.key] =
                              await convertFileToBase64(value);
                          } catch (error) {
                            console.error(
                              "Error converting file to base64",
                              error
                            );
                            normalizedItem[field.key] = null;
                          }
                        }

                        if (
                          field.key === "id" &&
                          (value === "" ||
                            value === null ||
                            value === undefined)
                        ) {
                          delete normalizedItem.id;
                        }
                      }

                      return normalizedItem;
                    })
                  );

                  handleStepSubmit(normalizedItems, step, index, "submit");
                }}
              />
            );
          }

          // For single-object steps:
          return (
            <DynamicForm
              title={step.title}
              fields={step.fields}
              defaultValues={defaultValues}
              onSubmit={(data) => handleStepSubmit(data, step, index)}
              showCancel={false}
              submitText="Save"
            />
          );
        }

        return (
          <div
            key={index}
            className={`mb-4 border rounded-lg shadow-sm ${
              isCompleted
                ? theme === "night"
                  ? "border-tertiary-600 bg-tertiary-900 text-gray-50"
                  : "border-tertiary-500 bg-tertiary-50"
                : theme === "night"
                ? "border-primary-600 bg-primary-900 text-gray-50"
                : "border-primary-500 bg-gray-50"
            }`}
          >
            <div
              onClick={() => toggleStep(index)}
              className={`flex items-center justify-between px-4 py-3 cursor-pointer rounded-t-lg ${
                isCompleted
                  ? theme === "night"
                    ? "bg-tertiary-800 text-tertiary-100"
                    : "bg-tertiary-100 text-tertiary-700"
                  : theme === "night"
                  ? "bg-primary-600 text-primary-100"
                  : "bg-primary-100 text-primary-700"
              }`}
            >
              <div className="flex items-center space-x-2 font-semibold">
                <span
                  className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xl font-bold border-2 ${
                    isCompleted
                      ? theme === "night"
                        ? "border-tertiary-400 text-tertiary-100"
                        : "border-tertiary-600 text-tertiary-600"
                      : theme === "night"
                      ? "border-primary-400 text-primary-100"
                      : "border-primary-600 text-primary-600"
                  }`}
                >
                  {index + 1}
                </span>
                {step.icon && <i className="material-icons">{step.icon}</i>}
                <span>{step.title || `Step ${index + 1}`}</span>
              </div>
              <span className="text-2xl">{isExpanded ? "-" : "+"}</span>
            </div>

            {isExpanded && (
              <div
                className={`p-4 rounded-b-lg ${
                  theme === "night" ? "bg-gray-800 text-gray-100" : "bg-white"
                }`}
              >
                {renderStepContent(step, index)}
              </div>
            )}
          </div>
        );
      })}

      {onCancel && (
        <div className="mt-8">
          <button
            onClick={onCancel}
            className="rounded bg-gray-400 text-white px-4 py-2 text-sm"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default AccordionForm;
